import { z } from 'zod';
import { LEAD_SOURCES, LEAD_STAGES, PROCEDURE_AREAS } from './crm.js';
export const AUTOMATION_TRIGGERS = ['lead_created', 'lead_stage_changed', 'tag_added'];
const MAX_WAIT_MIN = 90 * 24 * 60; // 90 dias
export const automationStepSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('send_message'), text: z.string().trim().min(1, 'Escreva a mensagem').max(4096) }),
    z.object({ type: z.literal('wait'), minutes: z.number().int().min(1).max(MAX_WAIT_MIN) }),
    z.object({ type: z.literal('add_tag'), tagId: z.uuid('Escolha a etiqueta') }),
    /** "perdido" fica de fora: exige motivo, que só a equipe informa */
    z.object({ type: z.literal('move_stage'), stage: z.enum(['new', 'qualifying', 'evaluation_scheduled', 'evaluated', 'quoted', 'won']) }),
    /** userId null = próximo do rodízio da distribuição */
    z.object({ type: z.literal('assign'), userId: z.uuid().nullable() }),
]);
export const automationConditionsSchema = z.object({
    sources: z.array(z.enum(LEAD_SOURCES)).max(20).optional(),
    areas: z.array(z.enum(PROCEDURE_AREAS)).max(10).optional(),
    tagIds: z.array(z.uuid()).max(20).optional(),
});
export const upsertAutomationSchema = z.object({
    name: z.string().trim().min(2, 'Dê um nome').max(80),
    enabled: z.boolean(),
    trigger: z.enum(AUTOMATION_TRIGGERS),
    triggerValue: z.string().nullable(),
    conditions: automationConditionsSchema,
    steps: z.array(automationStepSchema).min(1, 'Adicione pelo menos um passo').max(20),
    stopOnReply: z.boolean(),
}).superRefine((v, ctx) => {
    if (v.trigger === 'lead_stage_changed' && !LEAD_STAGES.includes(v.triggerValue ?? '')) {
        ctx.addIssue({ code: 'custom', path: ['triggerValue'], message: 'Escolha a etapa' });
    }
    if (v.trigger === 'tag_added' && !z.uuid().safeParse(v.triggerValue).success) {
        ctx.addIssue({ code: 'custom', path: ['triggerValue'], message: 'Escolha a etiqueta' });
    }
    if (!v.steps.some((s) => s.type !== 'wait')) {
        ctx.addIssue({ code: 'custom', path: ['steps'], message: 'A automação precisa fazer algo além de esperar' });
    }
});
export const automationSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    enabled: z.boolean(),
    trigger: z.enum(AUTOMATION_TRIGGERS),
    triggerValue: z.string().nullable(),
    conditions: automationConditionsSchema,
    steps: z.array(automationStepSchema),
    stopOnReply: z.boolean(),
    stats: z.object({ running: z.number().int(), done: z.number().int(), cancelled: z.number().int(), failed: z.number().int() }),
    updatedAt: z.string(),
});
export const automationRunSchema = z.object({
    id: z.uuid(),
    leadId: z.uuid(),
    contactName: z.string(),
    status: z.enum(['waiting', 'done', 'cancelled', 'failed']),
    stepIndex: z.number().int(),
    nextAt: z.string(),
    startedAt: z.string(),
    finishedAt: z.string().nullable(),
    cancelReason: z.string().nullable(),
    lastError: z.string().nullable(),
});
// ------------------------------------------------------------ disparos em massa
export const broadcastAudienceSchema = z.object({
    tagIds: z.array(z.uuid()).max(20).optional(),
    stages: z.array(z.enum(LEAD_STAGES)).max(10).optional(),
    sources: z.array(z.enum(LEAD_SOURCES)).max(20).optional(),
});
export const upsertBroadcastSchema = z.object({
    name: z.string().trim().min(2, 'Dê um nome').max(80),
    channelAccountId: z.uuid('Escolha o número'),
    text: z.string().trim().min(1, 'Escreva a mensagem').max(3900),
    audience: broadcastAudienceSchema,
    intervalSeconds: z.number().int().min(8).max(600),
    dailyLimit: z.number().int().min(1).max(2000),
});
export const BROADCAST_STATUSES = ['draft', 'sending', 'paused', 'done', 'cancelled'];
export const broadcastSchema = upsertBroadcastSchema.safeExtend({
    id: z.uuid(),
    status: z.enum(BROADCAST_STATUSES),
    channelName: z.string(),
    total: z.number().int(), sent: z.number().int(), failed: z.number().int(), skipped: z.number().int(),
    /** das enviadas: entregues/lidas (status da mensagem) e respondidas (paciente escreveu depois) */
    delivered: z.number().int(), read: z.number().int(), replied: z.number().int(),
    nextSendAt: z.string().nullable(),
    createdAt: z.string(), startedAt: z.string().nullable(), finishedAt: z.string().nullable(),
});
export const broadcastPreviewSchema = z.object({
    recipients: z.number().int(),
    optedOut: z.number().int(),
    withoutPhone: z.number().int(),
    sample: z.array(z.string()),
});
export const broadcastRecipientSchema = z.object({
    contactId: z.uuid(), name: z.string(),
    status: z.enum(['queued', 'sent', 'failed', 'skipped']),
    delivery: z.enum(['queued', 'sent', 'delivered', 'read', 'failed']).nullable(),
    replied: z.boolean(), sentAt: z.string().nullable(), error: z.string().nullable(),
});
/** Textos fixos do disparo (o rodapé sai em toda mensagem de campanha). */
export const BROADCAST_FOOTER = 'Para não receber mais mensagens como esta, responda SAIR.';
//# sourceMappingURL=automations.js.map